import React, { useState } from 'react';

interface DisplayImageProps {
  images: Array<{
    localPath: string;
    isMain?: boolean;
    alt?: string;
  }>;
  className?: string;
  imgClassName?: string;
  thumbnailClassName?: string;
  thumbnailImgClassName?: string;
  style?: React.CSSProperties;
  thumbnailsClickable?: boolean;
  /** Called with the index of the currently-displayed image when the main image is clicked. */
  onMainImageClick?: (index: number) => void;
}

export const DisplayImage: React.FC<DisplayImageProps> = ({
  images = [],
  className = '',
  imgClassName = '',
  thumbnailClassName = '',
  thumbnailImgClassName = 'w-8 h-8',
  style = {},
  thumbnailsClickable = true,
  onMainImageClick,
}) => {
  if (!images.length) return null;
  const mainIdx = images.findIndex((img) => img.isMain);
  const [selectedIdx, setSelectedIdx] = useState(mainIdx >= 0 ? mainIdx : 0);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const displayIdx = hoverIdx !== null ? hoverIdx : selectedIdx;
  const selected = images[displayIdx];

  const mainImg = (
    <img
      src={selected.localPath.replace(/^public\//, '/')}
      alt={selected.alt || 'Product image'}
      className={`object-contain rounded ${imgClassName}`}
    />
  );

  return (
    <div className={`flex flex-col items-center ${className}`} style={style}>
      {onMainImageClick ? (
        <button
          type="button"
          onClick={() => onMainImageClick(displayIdx)}
          className="block w-full bg-transparent border-0 p-0 cursor-zoom-in focus:outline-none"
          aria-label="View larger image"
        >
          {mainImg}
        </button>
      ) : mainImg}
      {images.length > 1 && (
        <div className="flex gap-2 mt-2 flex-wrap justify-center">
          {images.map((img, idx) => (
            <button
              key={img.localPath + idx}
              type="button"
              className={`border rounded p-0.5 bg-white focus:outline-none ${displayIdx === idx ? 'border-blue-500' : 'border-gray-300'} ${thumbnailClassName}`}
              onClick={thumbnailsClickable ? () => setSelectedIdx(idx) : undefined}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
              aria-label={`Show image ${idx + 1}`}
              tabIndex={thumbnailsClickable ? 0 : -1}
              style={thumbnailsClickable ? undefined : { cursor: 'inherit' }}
            >
              <img
                src={img.localPath.replace(/^public\//, '/')}
                alt={img.alt || `Thumbnail ${idx + 1}`}
                className={`object-contain rounded ${thumbnailImgClassName}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
